import cleaningAuditClient from "./cleaningAuditClient";
import { getCleaningAuditSession, invalidateCleaningAuditSession } from "./cleaningAuditAuth";
import { withLookupCache, clearLookups } from "utils/lookupCache";

// Thin wrapper around the Cleaning Audit backend, mirroring the shape of
// src/utils/apiCall.js (getApi/postApi -> { error, result }) so screens in this
// module read the same way the rest of the app's API calls do. Every call first
// resolves the Cleaning-Audit-specific session token (see cleaningAuditAuth.js)
// rather than reusing MyM's own token-id/client-id headers directly.

const caHeaders = async () => {
  const { tokenId, clientId } = await getCleaningAuditSession();
  return {
    "token-id": tokenId,
    "client-id": clientId,
    "device-type": process.env.REACT_APP_CA_DEVICE_TYPE,
  };
};

// Cleaning Audit's own role_id for the current user, resolved as part of the
// auth exchange (see cleaningAuditAuth.js) — needed by the state-by-role lookup.
export const getCleaningAuditRoleId = async () => {
  const { roleId } = await getCleaningAuditSession();
  return roleId;
};

// The Cleaning Audit backend can invalidate a session token we're still holding
// (see cleaningAuditAuth.js) — a 401 here doesn't necessarily mean the user is
// unauthorized, it can just mean our cached token went stale. Retry once, with
// a freshly re-minted session, before giving up.
const withRetryOn401 = async (makeRequest) => {
  try {
    const headers = await caHeaders();
    return await makeRequest(headers);
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    invalidateCleaningAuditSession();
    const headers = await caHeaders();
    return await makeRequest(headers);
  }
};

const fetchGet = async (url) => {
  try {
    const response = await withRetryOn401((headers) => cleaningAuditClient.get(url, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// Dropdown/master lists, cached briefly (see utils/lookupCache).
const LOOKUPS = /\/(managed_states_by_role|managed_facilities_by_state|managed_areas_risk_types|managed_que_cats|get_cleaning_report_type_data|managed_que_wheres_bank|managed_que_whys_bank)\b/;
export const getCaApi = (url) => withLookupCache("cleaning-audit", LOOKUPS, url, () => fetchGet(url));

export const postCaApi = async (url, data) => {
  clearLookups("cleaning-audit");
  try {
    const response = await withRetryOn401((headers) => cleaningAuditClient.post(url, data, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

export const putCaApi = async (url, data) => {
  clearLookups("cleaning-audit");
  try {
    const response = await withRetryOn401((headers) => cleaningAuditClient.put(url, data, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

export const deleteCaApi = async (url) => {
  clearLookups("cleaning-audit");
  try {
    const response = await withRetryOn401((headers) => cleaningAuditClient.delete(url, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// ---- Dashboard ----
// Paths match AuditController.js's own forwarding exactly (config_setting.cleaning_audit_api
// + 'managed_audits_count', etc.) — no /api prefix, no trailing slash; that shape
// belonged to the old server's local route registration, not the real API.
export const getLatestFiveAudits = () => getCaApi("/managed_latest_five_audits");
export const getAuditCount = () => getCaApi("/managed_audits_count");
export const getAuditGraphData = () => getCaApi("/managed_audits_graph_data");

// ---- Shared: state / facility lists ----
// role_id/state_id/que_id are query params on this API (see req.query.* in
// MajesticERP-CleaningAuditApi/code/controllers/CleaningUsersController.js,
// QuestionsController.js, QueWheresBankController.js, QueWhysBankController.js)
// — only facility-scoped and single-record lookups use a path segment (:id).
export const getStatesByRole = (roleId) => getCaApi(`/managed_states_by_role?role_id=${roleId}`);
export const getFacilitiesByState = (stateId) => getCaApi(`/managed_facilities_by_state/${stateId}`);

// ---- Tracking Report ----
export const getAuditTrackingReport = (body) => postCaApi("/get_cleaning_audit_traking_details", body);

// ---- Audit Register ----
export const getFacilityAuditCompletion = (params) => {
  const queryParams = [];
  if (params.year) queryParams.push(`year=${params.year}`);
  if (params.state_id) queryParams.push(`state_id=${params.state_id}`);
  if (params.facility_id) queryParams.push(`facility_id=${params.facility_id}`);
  const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
  return getCaApi(`/get_facility_audit_completion${queryString}`);
};

// ---- Reports ----
export const getReportTypes = () => getCaApi("/get_cleaning_report_type_data");
export const getCleaningAuditReports = (body) => postCaApi("/get_cleaning_audit_report_data", body);
export const getPresignedReportUrl = (pdfUrl) => postCaApi("/get_presigned_url", { url: pdfUrl });

// ---- Questions ("State Questions" and "Facility Questions" share
// managed_questions: state questions have facility_id "0", facility questions
// a real facility_id. Asking for a facility returns that facility's questions
// PLUS the state's (facility_id in (0, id)), which is what an audit needs; the
// Facility Questions screen shows only the facility's own rows.) ----
export const getQuestionsByState = (stateId, facilityId = "0") => getCaApi(`/managed_questions?state_id=${stateId}&facility_id=${facilityId}`);
export const addQuestion = (body) => postCaApi("/managed_questions", body);
export const updateQuestion = (body) => putCaApi("/managed_questions", body);
export const deleteQuestion = (id) => deleteCaApi(`/managed_questions/${id}`);

// ---- Audit Question Configuration: a question's category for each of the
// state's area risk types (ca014_area_risk_type_que_cat). Audit scoring uses
// the category's weighting, so every risk type needs one. One row per risk
// type, keyed by (que_id, area_risk_type_id): POST adds it, PUT changes it. ----
export const getQuestionCategories = () => getCaApi("/managed_que_cats");
export const getQuestionConfig = (questionId) => getCaApi(`/managed_area_risk_que/${questionId}`);
export const addQuestionConfig = (body) => postCaApi("/managed_area_risk_que", body); // {que_id, area_risk_type_id, que_cat_id}
export const updateQuestionConfig = (body) => putCaApi("/managed_area_risk_que", body);

// ---- Observation Questions: their own list (table ca016_observation_ques),
// separate from the state questions above. Optional, so often empty. ----
export const getObservationQuestions = (stateId) => getCaApi(`/managed_observation_ques?state_id=${stateId}`);
export const addObservationQuestion = (body) => postCaApi("/add_observation_question", body); // {state_id, que_text, que_info}
export const updateObservationQuestion = (body) => putCaApi("/edit_observation_question", body); // {id, que_text, que_info}
export const deleteObservationQuestion = (id) => deleteCaApi(`/delete_observation_questions/${id}`);

export const getQuestionWhereBank = (stateId, questionId) => getCaApi(`/managed_que_wheres_bank?state_id=${stateId}&que_id=${questionId}`);
export const addQuestionWhere = (body) => postCaApi("/managed_que_wheres_bank", body);
export const updateQuestionWhere = (body) => putCaApi("/managed_que_wheres_bank", body);
export const deleteQuestionWhere = (id) => deleteCaApi(`/managed_que_wheres_bank/${id}`);

export const getQuestionWhyBank = (stateId, questionId) => getCaApi(`/managed_que_whys_bank?state_id=${stateId}&que_id=${questionId}`);
export const addQuestionWhy = (body) => postCaApi("/managed_que_whys_bank", body);
export const updateQuestionWhy = (body) => putCaApi("/managed_que_whys_bank", body);
export const deleteQuestionWhy = (id) => deleteCaApi(`/managed_que_whys_bank/${id}`);

// ---- Facility Setup: floors / areas / rooms ----
export const getAreaRiskTypes = (stateId) => getCaApi(`/managed_areas_risk_types/${stateId}`);

export const getFloorsByFacility = (facilityId) => getCaApi(`/managed_floors_by_facility/${facilityId}`);
export const addFloor = (body) => postCaApi("/managed_floors", body);
export const updateFloor = (body) => putCaApi("/managed_floors", body);
export const deleteFloor = (id) => deleteCaApi(`/managed_floors/${id}`);

export const getAreasByFacility = (facilityId) => getCaApi(`/managed_areas_by_facility/${facilityId}`);
export const addArea = (body) => postCaApi("/managed_areas", body);
export const updateArea = (body) => putCaApi("/managed_areas", body);
export const deleteArea = (id) => deleteCaApi(`/managed_areas/${id}`);

export const getRoomsByFacility = (facilityId) => getCaApi(`/managed_rooms_by_facility/${facilityId}`);
export const addRoom = (body) => postCaApi("/managed_rooms", body);
export const updateRoom = (body) => putCaApi("/managed_rooms", body);
export const deleteRoom = (id) => deleteCaApi(`/managed_rooms/${id}`);

// Bulk upload: a CSV (base64, no data: prefix) of State, Facility, Floor, Area
// Name, Area Type, Room Name. Answers { processedData, errors }.
export const bulkUploadFacilitySetup = (file) => postCaApi("/managed_bulk_upload", { file });
